import { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Pencil, Trash2, FileText, Download, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  getInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  recordInvoicePayment,
  resetInvoicePayment,
  getBankAccounts,
  formatCurrency,
  formatDate,
  handleNumericInput,
  parseNumericValue,
  formatIndianNumber,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";
import GSTInput from "../components/GSTInput";
import { DatePicker } from "../components/DatePicker";

// Helper to add one month to a date
const addOneMonth = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().split("T")[0];
};

// Helper to check if invoice is overdue
const isOverdue = (invoice) => {
  if (invoice.status === "paid") return false;
  if (!invoice.due_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(invoice.due_date);
  return dueDate < today;
};

// Helper to get display status (checks for overdue)
const getDisplayStatus = (invoice) => {
  if (invoice.status === "paid") return "paid";
  if (isOverdue(invoice)) return "overdue";
  return "pending";
};

const emptyInvoice = {
  company: "empact-studio",
  invoice_number: "",
  client_name: "",
  description: "",
  base_amount: "",
  cgst_rate: "",
  sgst_rate: "",
  igst_rate: "",
  tds_rate: "",
  status: "pending",
  invoice_date: new Date().toISOString().split("T")[0],
  due_date: addOneMonth(new Date().toISOString().split("T")[0]),
};

const StatusBadge = ({ status, isOverdueItem }) => {
  const statusClasses = {
    pending: "bg-amber-100 text-amber-700",
    partial: "bg-blue-100 text-blue-700",
    paid: "bg-emerald-100 text-emerald-700",
    overdue: "bg-red-100 text-red-700",
  };
  
  const displayStatus = isOverdueItem ? "overdue" : status;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[displayStatus] || statusClasses.pending}`}>
      {displayStatus === "overdue" && <AlertTriangle className="w-3 h-3" />}
      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
    </span>
  );
};

export default function Invoices() {
  const { canEdit } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState(emptyInvoice);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [receiptAmounts, setReceiptAmounts] = useState({});
  const [selectedBankAccounts, setSelectedBankAccounts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const [invoicesRes, bankRes] = await Promise.all([
        getInvoices(),
        getBankAccounts()
      ]);
      setInvoices(invoicesRes.data);
      setBankAccounts(bankRes.data);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setSelectedInvoice(invoice);
      setFormData({
        company: invoice.company || "empact-studio",
        invoice_number: invoice.invoice_number,
        client_name: invoice.client_name,
        description: invoice.description,
        base_amount: invoice.base_amount,
        cgst_rate: invoice.cgst_rate,
        sgst_rate: invoice.sgst_rate,
        igst_rate: invoice.igst_rate,
        status: invoice.status,
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
      });
    } else {
      const todayStr = new Date().toISOString().split("T")[0];
      setSelectedInvoice(null);
      setFormData({
        ...emptyInvoice, 
        company: filterCompany !== "all" ? filterCompany : "empact-studio",
        invoice_date: todayStr,
        due_date: addOneMonth(todayStr),
      });
    }
    setDialogOpen(true);
  };

  // Handle invoice date change - auto update due date to 1 month later
  const handleInvoiceDateChange = (newDate) => {
    setFormData({
      ...formData,
      invoice_date: newDate,
      due_date: addOneMonth(newDate),
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
    setFormData(emptyInvoice);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert empty string amounts to numbers for submission
      const submitData = {
        ...formData,
        base_amount: parseNumericValue(formData.base_amount),
        cgst_rate: parseNumericValue(formData.cgst_rate),
        sgst_rate: parseNumericValue(formData.sgst_rate),
        igst_rate: parseNumericValue(formData.igst_rate),
        tds_rate: parseNumericValue(formData.tds_rate),
      };
      
      if (selectedInvoice) {
        await updateInvoice(selectedInvoice.id, submitData);
        toast.success("Invoice updated successfully");
      } else {
        await createInvoice(submitData);
        toast.success("Invoice created successfully");
      }
      handleCloseDialog();
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to save invoice");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInvoice(selectedInvoice.id);
      toast.success("Invoice deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // Handle receipt amount change
  const handleReceiptAmountChange = (invoiceId, value) => {
    setReceiptAmounts(prev => ({
      ...prev,
      [invoiceId]: handleNumericInput(value)
    }));
  };

  // Handle bank account selection
  const handleBankAccountChange = (invoiceId, bankAccountId) => {
    setSelectedBankAccounts(prev => ({
      ...prev,
      [invoiceId]: bankAccountId
    }));
  };

  // Record a payment receipt
  const handleRecordReceipt = async (invoice) => {
    const amountStr = receiptAmounts[invoice.id];
    const amount = parseNumericValue(amountStr);
    const bankAccountId = selectedBankAccounts[invoice.id];
    
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount received");
      return;
    }
    
    if (!bankAccountId) {
      toast.error("Please select a bank account to receive payment");
      return;
    }
    
    try {
      setSavingId(invoice.id);
      await recordInvoicePayment(invoice.id, amount, bankAccountId);
      toast.success(`Receipt of ${formatCurrency(amount)} recorded`);
      setReceiptAmounts(prev => {
        const newState = { ...prev };
        delete newState[invoice.id];
        return newState;
      });
      setSelectedBankAccounts(prev => {
        const newState = { ...prev };
        delete newState[invoice.id];
        return newState;
      });
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to record receipt");
    } finally {
      setSavingId(null);
    }
  };

  // Reset receipt (undo)
  const handleResetReceipt = async (invoice) => {
    try {
      setSavingId(invoice.id);
      await resetInvoicePayment(invoice.id);
      toast.success("Receipt reset (bank balance adjusted)");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to reset receipt");
    } finally {
      setSavingId(null);
    }
  };

  const filteredInvoices = invoices.filter((inv) => {
    const overdueStatus = isOverdue(inv);
    const displayStatus = getDisplayStatus(inv);
    
    let statusMatch = false;
    if (filterStatus === "all") {
      statusMatch = true;
    } else if (filterStatus === "overdue") {
      statusMatch = overdueStatus;
    } else if (filterStatus === "partial") {
      statusMatch = inv.status === "partial";
    } else {
      statusMatch = inv.status === filterStatus && (filterStatus === "paid" || !overdueStatus);
    }
    
    const companyMatch = filterCompany === "all" || inv.company === filterCompany;
    return statusMatch && companyMatch;
  });

  // Count overdue invoices
  const overdueCount = invoices.filter(inv => isOverdue(inv) && inv.status !== "paid").length;

  // Calculate totals for partial payments
  const totalNetReceivable = filteredInvoices.reduce((sum, inv) => 
    sum + (inv.net_receivable || inv.total_amount - (inv.tds_amount || 0)), 0);
  const totalReceived = filteredInvoices.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
  const totalBalance = filteredInvoices.reduce((sum, inv) => {
    const netReceivable = inv.net_receivable || inv.total_amount - (inv.tds_amount || 0);
    return sum + (inv.balance !== undefined ? inv.balance : netReceivable - (inv.amount_received || 0));
  }, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track receivables with GST and TDS breakdown
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-invoices"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="add-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Net Receivable</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-slate-900 tabular-nums">
                    {formatCurrency(totalNetReceivable)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Received So Far</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-emerald-600 tabular-nums">
                    {formatCurrency(totalReceived)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Balance Pending</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-amber-600 tabular-nums">
                    {formatCurrency(totalBalance)}
                  </p>
                  {overdueCount > 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      {overdueCount} overdue
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={filterCompany} onValueChange={setFilterCompany}>
                  <SelectTrigger className="w-40" data-testid="filter-invoice-company">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANIES.map((company) => (
                      <SelectItem key={company.value} value={company.value}>
                        {company.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-32" data-testid="filter-invoice-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Received</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Company Breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100">
              {COMPANY_OPTIONS.map((company) => {
                const companyBalance = invoices
                  .filter((inv) => inv.company === company.value && inv.status !== "paid")
                  .reduce((sum, inv) => {
                    const netReceivable = inv.net_receivable || inv.total_amount - (inv.tds_amount || 0);
                    return sum + (inv.balance !== undefined ? inv.balance : netReceivable - (inv.amount_received || 0));
                  }, 0);
                return (
                  <div key={company.value} className={`p-3 rounded-lg ${COMPANY_COLORS[company.value]}`}>
                    <p className="text-xs font-medium opacity-80">{company.label}</p>
                    <p className="text-sm sm:text-lg font-bold tabular-nums">{formatCurrency(companyBalance)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredInvoices.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[1000px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Base Amount</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net Receivable</TableHead>
                  <TableHead className="text-right w-40">Receive Amount</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center w-32">Action</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => {
                  const overdueStatus = isOverdue(invoice) && invoice.status !== "paid";
                  const baseAmount = invoice.base_amount || 0;
                  const gstAmount = (invoice.cgst_amount || 0) + (invoice.sgst_amount || 0) + (invoice.igst_amount || 0);
                  const tdsAmount = invoice.tds_amount || 0;
                  const netReceivable = invoice.net_receivable || (invoice.total_amount - tdsAmount);
                  const receivedSoFar = invoice.amount_received || 0;
                  const balance = invoice.balance !== undefined ? invoice.balance : (netReceivable - receivedSoFar);
                  const isFullyReceived = balance <= 0;
                  const receiptAmount = receiptAmounts[invoice.id] || "";
                  
                  return (
                  <TableRow 
                    key={invoice.id} 
                    data-testid={`invoice-row-${invoice.id}`}
                    className={
                      isFullyReceived 
                        ? "bg-emerald-50/50" 
                        : invoice.status === "partial"
                          ? "bg-blue-50/50"
                          : overdueStatus 
                            ? "bg-red-50/70" 
                            : ""
                    }
                  >
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[invoice.company] || COMPANY_COLORS.all}`}>
                        {getCompanyLabel(invoice.company)}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{invoice.client_name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[120px]">
                          {invoice.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-700">
                      {formatCurrency(baseAmount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-blue-600">
                      {gstAmount > 0 ? (
                        <>
                          +{formatCurrency(gstAmount)}
                          <span className="text-xs text-blue-400 block">
                            {invoice.cgst_amount > 0 && `C:${invoice.cgst_rate}%`}
                            {invoice.sgst_amount > 0 && ` S:${invoice.sgst_rate}%`}
                            {invoice.igst_amount > 0 && ` I:${invoice.igst_rate}%`}
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {tdsAmount > 0 ? (
                        <>
                          -{formatCurrency(tdsAmount)}
                          <span className="text-xs text-red-400 block">
                            @{invoice.tds_rate}%
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                      {formatCurrency(netReceivable)}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isFullyReceived ? (
                        <div className="flex flex-col gap-1">
                          <div className="relative inline-flex">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={receiptAmount}
                              onChange={(e) => handleReceiptAmountChange(invoice.id, e.target.value)}
                              onBlur={(e) => {
                                const val = parseNumericValue(e.target.value);
                                if (val > 0) {
                                  setReceiptAmounts(prev => ({...prev, [invoice.id]: formatIndianNumber(val)}));
                                }
                              }}
                              className="text-right pr-3 pl-7 w-36 font-medium"
                              placeholder={formatIndianNumber(balance)}
                              data-testid={`receipt-input-${invoice.id}`}
                            />
                          </div>
                          <Select
                            value={selectedBankAccounts[invoice.id] || ""}
                            onValueChange={(value) => handleBankAccountChange(invoice.id, value)}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs" data-testid={`bank-select-${invoice.id}`}>
                              <SelectValue placeholder="To Bank..." />
                            </SelectTrigger>
                            <SelectContent>
                              {bankAccounts.map((bank) => (
                                <SelectItem key={bank.id} value={bank.id} className="text-xs">
                                  {bank.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={receivedSoFar > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                        {formatCurrency(receivedSoFar)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={balance > 0 ? (overdueStatus ? "text-red-600" : "text-amber-600") + " font-semibold" : "text-emerald-600 font-semibold"}>
                        {formatCurrency(balance)}
                      </span>
                    </TableCell>
                    <TableCell className={overdueStatus ? "text-red-600 font-medium" : ""}>
                      {formatDate(invoice.due_date)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={invoice.status} isOverdueItem={overdueStatus} />
                    </TableCell>
                    <TableCell className="text-center">
                      {isFullyReceived ? (
                        <Button
                          onClick={() => handleResetReceipt(invoice)}
                          disabled={savingId === invoice.id}
                          size="sm"
                          variant="outline"
                          className="h-9 px-3"
                          data-testid={`reset-btn-${invoice.id}`}
                        >
                          {savingId === invoice.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            "Undo"
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleRecordReceipt(invoice)}
                          disabled={savingId === invoice.id || !receiptAmount}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3"
                          data-testid={`receive-btn-${invoice.id}`}
                        >
                          {savingId === invoice.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            "Receive"
                          )}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(invoice)}
                          data-testid={`edit-invoice-${invoice.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">No invoices yet</h3>
              <p className="empty-state-text">Create your first invoice to track receivables</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice ? "Edit Invoice" : "Create Invoice"}
            </DialogTitle>
            <DialogDescription>
              {selectedInvoice ? "Update the invoice details below." : "Fill in the details to create a new invoice."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company}
                onValueChange={(value) => setFormData({ ...formData, company: value })}
              >
                <SelectTrigger data-testid="select-invoice-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((company) => (
                    <SelectItem key={company.value} value={company.value}>
                      {company.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoice_number">Invoice Number</Label>
                <Input
                  id="invoice_number"
                  value={formData.invoice_number}
                  onChange={(e) =>
                    setFormData({ ...formData, invoice_number: e.target.value })
                  }
                  placeholder="INV-001"
                  required
                  data-testid="input-invoice-number"
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger data-testid="select-invoice-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="client_name">Client Name</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) =>
                  setFormData({ ...formData, client_name: e.target.value })
                }
                placeholder="Company Name"
                required
                data-testid="input-client-name"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Service/Product description"
                data-testid="input-description"
              />
            </div>

            <div>
              <Label htmlFor="base_amount">Base Amount (₹)</Label>
              <Input
                id="base_amount"
                type="text"
                inputMode="decimal"
                value={formData.base_amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    base_amount: handleNumericInput(e.target.value),
                  })
                }
                placeholder="Enter amount"
                required
                data-testid="input-base-amount"
              />
            </div>

            <GSTInput
              cgst_rate={formData.cgst_rate}
              sgst_rate={formData.sgst_rate}
              igst_rate={formData.igst_rate}
              handleNumericInput={handleNumericInput}
              onChange={(vals) => setFormData((prev) => ({ ...prev, ...vals }))}
            />

            <div>
              <Label htmlFor="tds_rate" className="text-red-600">TDS Rate (%) - Deducted by Client</Label>
              <Select
                value={formData.tds_rate?.toString() || "0"}
                onValueChange={(value) => setFormData({ ...formData, tds_rate: value })}
              >
                <SelectTrigger data-testid="input-tds-rate">
                  <SelectValue placeholder="Select TDS Rate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No TDS</SelectItem>
                  <SelectItem value="1">1% - Insurance Commission</SelectItem>
                  <SelectItem value="2">2% - Contractors</SelectItem>
                  <SelectItem value="5">5% - Commission</SelectItem>
                  <SelectItem value="10">10% - Professional Fees</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">TDS deducted by client before payment</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoice_date">Invoice Date</Label>
                <DatePicker
                  value={formData.invoice_date}
                  onChange={(val) => handleInvoiceDateChange(val)}
                  data-testid="input-invoice-date"
                />
              </div>
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <DatePicker
                  value={formData.due_date}
                  onChange={(val) => setFormData({ ...formData, due_date: val })}
                  data-testid="input-due-date"
                />
                <p className="text-xs text-slate-500 mt-1">Auto-set to 1 month from invoice date</p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
                data-testid="save-invoice"
              >
                {selectedInvoice ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice "{selectedInvoice?.invoice_number}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-invoice"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="invoices"
        title="Invoices"
        showDateFilter={true}
        showCompanyFilter={true}
      />
    </div>
  );
}
